import React, { useEffect, useState } from "react";
import { backendURL } from "../../config/constants";
import { useAuth } from "../../providers/AuthContext";

const parseDomains = (value) =>
  (value || "")
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);

const DomainList = ({ domains }) =>
  domains.length > 0 ? (
    domains.map((d, i) => (
      <span key={d}>
        <code>{d}</code>
        {i < domains.length - 1 && ", "}
      </span>
    ))
  ) : (
    <span>none</span>
  );

// Lists the exact values accepted for the "domain" field.
// Logged in: the domains of the user's active subscription (or free plan).
// Logged out: the domains of each public plan.
const AllowedDomains = () => {
  const { isAuthenticated, isLoading: authLoading, getAccessToken } = useAuth();
  const [userPlan, setUserPlan] = useState(null); // { name, domains }
  const [plans, setPlans] = useState([]); // [{ name, domains }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;

    const fetchPublicPlans = async () => {
      const res = await fetch(`${backendURL}/payments/plans`);
      if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
      const data = await res.json();
      return (Array.isArray(data) ? data : []).map((plan) => ({
        name: plan.name,
        domains: parseDomains(plan.allowed_domains),
      }));
    };

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isAuthenticated) {
          const res = await fetch(`${backendURL}/payments/active-subscription`, {
            headers: { Authorization: `Bearer ${getAccessToken()}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) {
              setUserPlan({
                name: data?.plan?.name || data?.plan_name || "Free Plan",
                domains: parseDomains(
                  data?.plan?.allowed_domains || data?.allowed_domains,
                ),
              });
            }
            return;
          }
        }
        const publicPlans = await fetchPublicPlans();
        if (!cancelled) {
          setUserPlan(null);
          setPlans(publicPlans);
        }
      } catch (err) {
        console.error("Error fetching allowed domains:", err);
        if (!cancelled) setError("Failed to load allowed domains.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, getAccessToken]);

  return (
    <div>
      <p>
        <strong className="supported-color">Domain values:</strong> pass one of
        these exactly as <code>domain</code>. Any other value returns 403.
      </p>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : userPlan ? (
        <p>
          Your plan ({userPlan.name}): <DomainList domains={userPlan.domains} />
        </p>
      ) : (
        <>
          <ul className="docs-list">
            {plans.map((plan) => (
              <li key={plan.name}>
                {plan.name}: <DomainList domains={plan.domains} />
              </li>
            ))}
          </ul>
          <p>Log in to see the domains for your own plan.</p>
        </>
      )}
    </div>
  );
};

export default AllowedDomains;
